/**
 * Settings "Profile" card: the name and picture other people see. Saves through
 * `PATCH /api/user/me` and `PUT`/`DELETE /api/user/me/avatar`; the server validates both and
 * this card only shows its answer. Successful edits update the user store, so the header changes
 * at once.
 *
 * @module apps/web/src/components/settings/ProfileCard
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ImagePlus, Trash2, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { AVATAR_MAX_SOURCE_BYTES, squareAvatarBlob } from '@/lib/avatarImage';
import { useUserStore } from '@/stores/userStore';

/** Longest name the field accepts; the server enforces the same 120-character limit. */
const MAX_NAME_LENGTH = 120;

/** Edit the signed-in person's display name and profile picture. */
export function ProfileCard() {
  const { toast } = useToast();
  const displayName = useUserStore(s => s.displayName);
  const email = useUserStore(s => s.email);
  const avatarUrl = useUserStore(s => s.avatarUrl);
  const setProfile = useUserStore(s => s.setProfile);
  const [name, setName] = useState(displayName || '');
  const [busy, setBusy] = useState<'name' | 'picture' | null>(null);
  const [error, setError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  // Refresh from the server once, so another device's edit shows here too.
  useEffect(() => {
    let current = true;
    api.user.getProfile().then(response => {
      const user = response.data?.user;
      if (!current || !response.success || !user) return;
      setProfile({ displayName: user.display_name ?? null, avatarUrl: user.avatar_url ?? null });
      setName(user.display_name || '');
    });
    return () => {
      current = false;
    };
  }, [setProfile]);

  /** Save the name and show the server's validation message when it refuses. */
  async function saveName(event: FormEvent) {
    event.preventDefault();
    setBusy('name');
    setError('');
    try {
      const response = await api.user.updateProfile({ display_name: name });
      if (!response.success)
        throw new Error(response.error?.message || 'Could not save your name.');
      const saved = response.data?.user?.display_name ?? name.trim();
      setProfile({ displayName: saved });
      setName(saved);
      toast({ title: 'Name saved', description: 'Your teams will see the new name.' });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save your name.');
    } finally {
      setBusy(null);
    }
  }

  /** Crop the chosen file to a square, upload it, and show the new picture. */
  async function uploadPicture(file?: File) {
    if (!file) return;
    setError('');
    if (
      !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
      file.size > AVATAR_MAX_SOURCE_BYTES
    ) {
      setError('Choose a PNG, JPEG, or WebP image no larger than 2 MB.');
      return;
    }
    setBusy('picture');
    try {
      const square = await squareAvatarBlob(file);
      const response = await api.user.uploadAvatar(square);
      if (!response.success || !response.data?.avatar_url) {
        throw new Error(response.error?.message || 'Could not upload the picture.');
      }
      setProfile({ avatarUrl: response.data.avatar_url });
      toast({ title: 'Picture updated' });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not upload the picture.');
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  /** Remove the picture and fall back to initials. */
  async function removePicture() {
    setBusy('picture');
    setError('');
    try {
      const response = await api.user.removeAvatar();
      if (!response.success)
        throw new Error(response.error?.message || 'Could not remove the picture.');
      setProfile({ avatarUrl: null });
      toast({ title: 'Picture removed' });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not remove the picture.');
    } finally {
      setBusy(null);
    }
  }

  const trimmed = name.trim();
  const unchanged = trimmed === (displayName || '').trim();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserRound className="h-5 w-5" />
          Profile
        </CardTitle>
        <CardDescription>The name and picture your teams and organization see.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <UserAvatar
            name={displayName}
            email={email}
            avatarUrl={avatarUrl}
            className="h-20 w-20 text-2xl"
          />
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              aria-label="Choose a profile picture"
              onChange={event => uploadPicture(event.target.files?.[0])}
            />
            <Button
              type="button"
              variant="outline"
              disabled={busy !== null}
              onClick={() => fileInput.current?.click()}
            >
              <ImagePlus className="mr-2 h-4 w-4" />
              {busy === 'picture' ? 'Saving…' : avatarUrl ? 'Change picture' : 'Upload picture'}
            </Button>
            {avatarUrl && (
              <Button
                type="button"
                variant="ghost"
                disabled={busy !== null}
                onClick={removePicture}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Remove
              </Button>
            )}
          </div>
        </div>
        <form className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end" onSubmit={saveName}>
          <div className="space-y-2">
            <Label htmlFor="profileDisplayName">Display name</Label>
            <Input
              id="profileDisplayName"
              value={name}
              maxLength={MAX_NAME_LENGTH}
              autoComplete="name"
              onChange={event => setName(event.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy !== null || !trimmed || unchanged}>
            {busy === 'name' ? 'Saving…' : 'Save name'}
          </Button>
        </form>
        {error && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
